import type { TenantInfo } from '@shared/types/restaurant';
import { safeSessionStorage } from '@shared/utils/safeStorage';

/**
 * Valid subdomain pattern: lowercase letters, digits, hyphens
 * 1-63 characters, cannot start or end with hyphen
 */
const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Domain configurations for different environments
 */
const DOMAIN_CONFIG = [
  { baseDomain: 'v2.meitre.com', apiBaseUrl: 'https://api.meitre.com', authDomain: 'auth.v2.meitre.com' },
  { baseDomain: 'meitre-test.com', apiBaseUrl: 'https://api-test.meitre.com', authDomain: 'auth.meitre-test.com' },
  { baseDomain: 'meitre-staging.com', apiBaseUrl: 'https://api-test.meitre.com', authDomain: 'auth.meitre-staging.com' },
  { baseDomain: 'meitre.com', apiBaseUrl: 'https://api.meitre.com', authDomain: 'auth.v2.meitre.com' },
] as const;

/**
 * Prefixes to ignore when extracting subdomain (e.g., www)
 */
const IGNORED_PREFIXES = ['www'];

/**
 * Validates if a string is a valid subdomain prefix
 */
export function isValidSubdomain(subdomain: string): boolean {
  if (!subdomain || subdomain.length === 0 || subdomain.length > 63) {
    return false;
  }
  return SUBDOMAIN_REGEX.test(subdomain.toLowerCase());
}

/**
 * Gets the current hostname from the browser
 */
export function getHostname(): string {
  return typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
}

/**
 * Gets the URL search params
 */
function getSearchParams(): URLSearchParams {
  return typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
}

/**
 * Determines if running in local development
 */
function isLocalDevelopment(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Extracts tenant info from a production or staging hostname
 *
 * Examples:
 * - pepito.meitre.com -> { subdomainPrefix: 'pepito', apiBaseUrl: 'https://api.meitre.com' }
 * - test.meitre-test.com -> { subdomainPrefix: 'test', apiBaseUrl: 'https://api-test.meitre.com' }
 * - meitre.com -> null (apex domain, no subdomain)
 * - www.meitre.com -> null (www treated as apex)
 */
function extractFromHostname(hostname: string): TenantInfo | null {
  // Check each domain configuration (most specific first)
  for (const config of DOMAIN_CONFIG) {
    const suffix = `.${config.baseDomain}`;
    if (!hostname.endsWith(suffix)) continue;

    // Remove the base domain to get potential subdomains
    // e.g., "pepito.meitre.com" -> "pepito"
    // e.g., "www.meitre.com" -> "www"
    const prefix = hostname.slice(0, -suffix.length);

    // If no prefix, it's the apex domain
    if (!prefix) return null;

    // Split by dots in case of nested subdomains (e.g., "sub.pepito")
    const parts = prefix.split('.').filter(Boolean);

    // Filter out ignored prefixes like www
    const validParts = parts.filter(part => !IGNORED_PREFIXES.includes(part));

    // We expect exactly one valid subdomain part
    if (validParts.length === 1 && isValidSubdomain(validParts[0])) {
      return {
        subdomainPrefix: validParts[0].toLowerCase(),
        apiBaseUrl: config.apiBaseUrl,
      };
    }

    return null;
  }

  // Hostname doesn't match any known domain
  return null;
}

const LOCAL_SUBDOMAIN_KEY = 'restaurant_slug';

/**
 * Extracts tenant info for local development
 *
 * Priority:
 * 1. ?r= query parameter (also persists to sessionStorage)
 * 2. sessionStorage (survives page reloads)
 * 3. VITE_DEFAULT_SUBDOMAIN environment variable
 *
 * API URL comes from VITE_API_BASE_URL or defaults to staging
 */
function extractForLocalDev(): TenantInfo | null {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api-test.meitre.com';

  // 1. Query parameter has highest priority: ?r=pepito
  const params = getSearchParams();
  const querySubdomain = params.get('r');

  if (querySubdomain && isValidSubdomain(querySubdomain)) {
    const subdomain = querySubdomain.toLowerCase();
    // Persist so it survives page reloads and navigation
    safeSessionStorage.setItem(LOCAL_SUBDOMAIN_KEY, subdomain);
    return { subdomainPrefix: subdomain, apiBaseUrl };
  }

  // 2. Stored subdomain from a previous ?r= param
  const stored = safeSessionStorage.getItem(LOCAL_SUBDOMAIN_KEY);
  if (stored && isValidSubdomain(stored)) {
    return { subdomainPrefix: stored.toLowerCase(), apiBaseUrl };
  }

  // 3. Environment variable fallback
  const envSubdomain = import.meta.env.VITE_DEFAULT_SUBDOMAIN;

  if (envSubdomain && isValidSubdomain(envSubdomain)) {
    return {
      subdomainPrefix: envSubdomain.toLowerCase(),
      apiBaseUrl,
    };
  }

  return null;
}

/**
 * Main function to extract tenant info (subdomain prefix and API URL) from the current URL.
 *
 * Handles:
 * - Production: pepito.meitre.com -> pepito (uses api.meitre.com)
 * - Staging: test.meitre-test.com -> test (uses api-test.meitre.com)
 * - Apex domains: meitre.com, www.meitre.com -> null (should redirect)
 * - Local dev: localhost with ?r=pepito or VITE_DEFAULT_SUBDOMAIN env var
 *
 * @returns TenantInfo object or null if no valid subdomain found
 */
export function getTenantInfo(): TenantInfo | null {
  const hostname = getHostname();

  // Local development handling
  if (isLocalDevelopment(hostname)) {
    return extractForLocalDev();
  }

  // Production/staging handling
  return extractFromHostname(hostname);
}

/**
 * Convenience function to get just the subdomain prefix
 * @deprecated Use getTenantInfo() instead for full tenant info including API URL
 */
export function getSubdomainPrefix(): string | null {
  const tenantInfo = getTenantInfo();
  return tenantInfo?.subdomainPrefix ?? null;
}

/**
 * Returns the domain config entry matching the current hostname.
 * Useful for deriving auth domain, base domain for links, etc.
 */
export function getCurrentDomainConfig() {
  const hostname = getHostname();
  for (const config of DOMAIN_CONFIG) {
    if (hostname.endsWith(`.${config.baseDomain}`) || hostname === config.baseDomain) {
      return config;
    }
  }
  return null;
}

/**
 * Gets the API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  const tenantInfo = getTenantInfo();

  // If we have tenant info, use its API URL
  if (tenantInfo) {
    return tenantInfo.apiBaseUrl;
  }

  // Fallback: try env var or default to production
  return import.meta.env.VITE_API_BASE_URL || 'https://api.meitre.com';
}
